"use client";

import { VariantProps, cva } from "class-variance-authority";
import { SearchIcon } from "icons";
import { useState, useEffect, useRef } from "react";
import {
  Input,
  Label,
  SearchField as AriaSearchField,
  Group,
} from "react-aria-components";
import { Spinner } from "ui-components";

interface IConversationSearchFieldProps
  extends VariantProps<typeof SearchFieldInputClass> {
  label?: string;
  placeholder?: string;
  debounce?: number;
  isLoading?: boolean;
  value?: string;
  onChange?: (value: string) => void;
  className?: string;
  isSearchButtonVisible?: boolean;
}

export function ConversationSearchField({
  label,
  placeholder,
  debounce = 300,
  isLoading = false,
  value = "",
  onChange,
  className,
}: Readonly<IConversationSearchFieldProps>): JSX.Element {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onChange?.(searchTerm);
    }, debounce);
    return () => clearTimeout(timeoutId);
  }, [debounce, searchTerm, onChange, value]);

  useEffect(() => {
    setSearchTerm(value);
  }, [value]);

  return (
    <AriaSearchField className={SearchFieldWrapperClass({})}>
      <Label>{label}</Label>
      <Group
        className={SearchFieldInputClass({
          className,
        })}
      >
        <SearchIcon aria-hidden className="w-5 h-5" />
        <Input
          className="outline-none w-full pr-4"
          ref={inputRef}
          placeholder={placeholder}
          value={searchTerm}
          onChange={({ target }) => setSearchTerm(target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur(); // This will remove focus from the input when Enter is pressed
            }
          }}
        />
        {isLoading && (
          <span className="mr-2.5">
            <Spinner />
          </span>
        )}
      </Group>
    </AriaSearchField>
  );
}

const SearchFieldWrapperClass = cva("w-full mb-5 ");
const SearchFieldInputClass = cva(
  "flex flex-row justify-start items-center gap-2 outline-none ml-4 "
);
